import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Edit3, Plus, Star, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Addresses() {
  const [addresses, setAddresses] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/addresses");
      setAddresses(data.addresses || []);
    } catch (e) {
      // not signed in
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!localStorage.getItem("jow_token")) return <Navigate to="/login" replace />;

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Address deleted.");
      load();
    } catch (err) {
      toast.error("Could not delete.");
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/addresses/${id}`, { is_default: true });
      toast.success("Default address updated.");
      load();
    } catch (err) {
      toast.error("Could not update.");
    }
  };

  return (
    <div className="space-y-6" data-testid="addresses-page">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-overline text-jow-gold">Address book</span>
          <h2 className="font-display text-3xl text-jow-navy mt-1">Where do we deliver?</h2>
        </div>
        {!showForm && !editingId && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary-jow"
            data-testid="addresses-add-btn"
          >
            <Plus size={14} /> Add address
          </button>
        )}
      </div>

      {(showForm || editingId) && (
        <AddressForm
          existing={editingId ? addresses.find((a) => a.id === editingId) : null}
          onCancel={() => {
            setShowForm(false);
            setEditingId(null);
          }}
          onSaved={() => {
            setShowForm(false);
            setEditingId(null);
            load();
          }}
        />
      )}

      {addresses.length === 0 && !showForm && (
        <div className="bg-white border border-jow-navy/10 p-8 text-center" data-testid="addresses-empty">
          <p className="font-display text-xl text-jow-navy">No addresses yet.</p>
          <p className="text-sm text-jow-navy/55 mt-2">
            Add one to make checkout a breeze.
          </p>
        </div>
      )}

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="addresses-list">
        {addresses.map((a) => (
          <li
            key={a.id}
            className={`bg-white border ${a.is_default ? "border-jow-gold" : "border-jow-navy/10"} p-5`}
            data-testid={`addr-row-${a.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display text-lg text-jow-navy">
                  {a.label} · {a.name}
                </p>
                <p className="text-sm text-jow-navy/70 mt-1">
                  {a.line1}
                  {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}
                </p>
                <p className="text-xs text-jow-navy/55 mt-1">{a.phone}</p>
              </div>
              {a.is_default && (
                <span className="label-overline text-jow-gold flex items-center gap-1">
                  <Star size={11} fill="#D4AF37" stroke="#D4AF37" /> Default
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 text-xs">
              <button
                onClick={() => setEditingId(a.id)}
                className="inline-flex items-center gap-1 label-overline text-jow-navy hover:text-jow-pink"
                data-testid={`addr-edit-${a.id}`}
              >
                <Edit3 size={11} /> Edit
              </button>
              {!a.is_default && (
                <button
                  onClick={() => setDefault(a.id)}
                  className="inline-flex items-center gap-1 label-overline text-jow-gold hover:underline"
                  data-testid={`addr-default-${a.id}`}
                >
                  <Star size={11} /> Make default
                </button>
              )}
              <button
                onClick={() => remove(a.id)}
                className="inline-flex items-center gap-1 label-overline text-jow-pink ml-auto"
                data-testid={`addr-delete-${a.id}`}
              >
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddressForm({ existing, onCancel, onSaved }) {
  const [form, setForm] = useState(
    existing || {
      label: "Home",
      name: "",
      phone: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      is_default: false,
    }
  );
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[1-9]\d{5}$/.test(form.pincode)) {
      toast.error("Invalid PIN code.");
      return;
    }
    setBusy(true);
    try {
      if (existing) {
        await api.patch(`/addresses/${existing.id}`, form);
        toast.success("Address updated.");
      } else {
        await api.post("/addresses", form);
        toast.success("Address saved.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-jow-gold/40 bg-jow-cream p-6"
      data-testid="addr-form"
    >
      <input className="input-jow border-b" placeholder="Label" value={form.label || ""} onChange={set("label")} />
      <input className="input-jow border-b" placeholder="Full name" value={form.name || ""} onChange={set("name")} required />
      <input className="input-jow border-b" placeholder="+91 …" value={form.phone || ""} onChange={set("phone")} required />
      <input className="input-jow border-b" placeholder="PIN" value={form.pincode || ""} onChange={set("pincode")} required />
      <input className="input-jow border-b md:col-span-2" placeholder="Line 1" value={form.line1 || ""} onChange={set("line1")} required />
      <input className="input-jow border-b md:col-span-2" placeholder="Line 2" value={form.line2 || ""} onChange={set("line2")} />
      <input className="input-jow border-b" placeholder="City" value={form.city || ""} onChange={set("city")} required />
      <input className="input-jow border-b" placeholder="State" value={form.state || ""} onChange={set("state")} required />
      <label className="flex items-center gap-2 text-sm md:col-span-2 text-jow-navy/70">
        <input
          type="checkbox"
          checked={!!form.is_default}
          onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
          className="accent-jow-pink"
        />
        Set as default
      </label>
      <div className="md:col-span-2 flex gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary-jow flex-1">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-primary-jow flex-1">
          {busy ? "Saving…" : existing ? "Update" : "Save"}
        </button>
      </div>
    </form>
  );
}
